import { useFeatureFlag } from '@agentnet/feature-flags';
import { ActivityRight } from 'api/profile/types';
import { useContext, useEffect, useMemo, useState } from 'react';
import {
  AgentNet_Admin,
  CAN_MANAGE_ESIGNATURES,
  INVENTORY_MANAGEMENT,
  INVOICE_DASHBOARD,
  Pay_Manual_Only,
  Pay_Rights,
  Remit_Rights,
  VIEW_AGENT_DASHBOARD_PREMIUM_REVENUE,
  VIEW_INVENTORY_MANAGEMENT,
  View_Pay,
} from 'utilities/constants/activity-rights';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';
import { RoleId } from 'utilities/constants/role-ids';
import { FileDataContext, FileDataContextInterface } from './FileDataContext';
import { ProfileContext, ProfileContextInterface } from './ProfileContext';

export const useNavPermissions = () => {
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { profile, userFirm, prismUserProfile } = profileCtx;
  const fileDataCtx: FileDataContextInterface = useContext(FileDataContext) ?? {};
  const {
    fileData,
    setFileData,
    setOpenRatesAndFeeDrawer,
    openRatesAndFeeDrawer,
    setOpenSaveModal,
    setRatesAndFeeDrawerClicked,
    isFormDirty,
  } = fileDataCtx;

  const [isApprovedAttorneyAccount, setIsApprovedAttorneyAccount] = useState<boolean>(false);
  const isMarketPlaceEnabled = true;
  const isAutopayEnabled = useFeatureFlag(FeatureFlag.SHOW_AUTOPAY, false);

  // ANS-6600: Approved Attorney Role Restrictions
  // Feature flag controls whether restrictions are applied
  // RoleId.APPROVED_ATTORNEY = "Approved Attorney" (new role, separate from existing clientType)
  const approvedAttorneyRestrictionsEnabled = useFeatureFlag(FeatureFlag.APPROVED_ATTORNEY_ROLE_RESTRICTIONS, false);
  const isRestrictedApprovedAttorney = profile?.roleId === RoleId.APPROVED_ATTORNEY;
  const shouldApplyApprovedAttorneyRestrictions =
    approvedAttorneyRestrictionsEnabled?.enabled && isRestrictedApprovedAttorney;

  useEffect(() => {
    const accountNumber = parseInt(window.sessionStorage.getItem('accountNumber') || '0', 10);
    if (fileData && userFirm?.offices)
      for (const office of userFirm.offices)
        for (const account of office.accounts)
          if (accountNumber === account.accountId) {
            setIsApprovedAttorneyAccount(account.clientType === 'Approved Attorney');
            break;
          }
  }, [fileData, userFirm]);

  const hasPermission = useMemo(() => {
    return {
      marketplace:
        profile?.activityRights?.some((rights: ActivityRight) => rights.ActivityRightId === AgentNet_Admin) &&
        isMarketPlaceEnabled,
      remit: shouldApplyApprovedAttorneyRestrictions
        ? false
        : profile?.activityRights?.some((rights: ActivityRight) => rights.ActivityRightId === Remit_Rights),
      pay: shouldApplyApprovedAttorneyRestrictions
        ? false
        : profile?.activityRights?.some(
            (rights: ActivityRight) =>
              rights.ActivityRightId === Pay_Rights ||
              rights.ActivityRightId === Pay_Manual_Only ||
              rights.ActivityRightId === View_Pay,
          ),
      viewAgentDashboardPremiumRevenue: profile?.activityRights?.some(
        (rights: ActivityRight) => rights.ActivityRightId === VIEW_AGENT_DASHBOARD_PREMIUM_REVENUE,
      ),
      payOnly: shouldApplyApprovedAttorneyRestrictions
        ? false
        : profile?.activityRights?.some((rights: ActivityRight) => rights.ActivityRightId === Pay_Manual_Only),
      billing: shouldApplyApprovedAttorneyRestrictions
        ? false
        : profile?.activityRights?.some(
            (rights: ActivityRight) =>
              rights.ActivityRightId === Pay_Rights || rights.ActivityRightId === Pay_Manual_Only,
          ),
      documents: Boolean(
        !(
          profile?.roleId === RoleId.USER &&
          profile?.roleTypeId === 101 &&
          profile?.activityRights?.some((right) => right.ActivityRightId === 125)
        ),
      ),
      cpl: shouldApplyApprovedAttorneyRestrictions
        ? false
        : Boolean(profile?.roleId !== RoleId.USER && profile?.roleId !== RoleId.STAFF_ADMIN),
      jackets: shouldApplyApprovedAttorneyRestrictions ? false : !isApprovedAttorneyAccount,
      manageUsers: profile?.activityRights?.some((right) => right.ActivityRightId === 36),
      behalfOfUser: Boolean(profile?.roleId === RoleId.SYSTEM_ADMIN || profile?.roleId === RoleId.ON_BEHALF_OF),
      notBehalfOfUser: Boolean(profile?.roleId === RoleId.USER || profile?.roleId === RoleId.STAFF_ADMIN),
      hasFirmId: Boolean(userFirm?.firmId),
      systemAdmin: Boolean(profile?.roleId === RoleId.SYSTEM_ADMIN),
      staffAdmin: Boolean(profile?.roleId === RoleId.STAFF_ADMIN),
      agentNetAdmin: profile?.activityRights?.some((right) => right.ActivityRightId === 36),
      backtitle: shouldApplyApprovedAttorneyRestrictions
        ? false
        : profile?.activityRights?.some(
            (rights: ActivityRight) => rights.ActivityRightId === 22 || rights.ActivityRightId === 32,
          ),
      canRunBackTitleAdHoc: shouldApplyApprovedAttorneyRestrictions
        ? false
        : profile?.activityRights?.some((rights: ActivityRight) => rights.ActivityRightId === 22),
      canRunBackTitleFiles: shouldApplyApprovedAttorneyRestrictions
        ? false
        : profile?.activityRights?.some((rights: ActivityRight) => rights.ActivityRightId === 32),
      canRunPolicyUpload: shouldApplyApprovedAttorneyRestrictions
        ? false
        : profile?.activityRights?.some((rights: ActivityRight) => rights.ActivityRightId === 49),
      filesList: Boolean(profile?.roleId !== RoleId.USER && profile?.roleId !== RoleId.STAFF_ADMIN),
      orderManagement: Boolean(profile?.roleId === RoleId.FSC_ORDER_MANAGEMENT),
      orderManagementAdmin: Boolean(
        profile?.roleId === RoleId.FSC_ORDER_MANAGEMENT &&
          profile?.activityRights?.some((right) => right.ActivityRightId === 138),
      ),
      crsUser: Boolean(
        profile?.roleId === RoleId.ON_BEHALF_OF &&
          profile?.activityRights?.some((right) => right.ActivityRightId === 137),
      ),
      accessOrderManagement: Boolean(
        profile?.roleId === RoleId.FSC_ORDER_MANAGEMENT ||
          (profile?.roleId === RoleId.ON_BEHALF_OF &&
            profile?.activityRights?.some((right) => right.ActivityRightId === 137)),
      ),
      orderManagementDashboard: Boolean(profile?.activityRights?.some((right) => right.ActivityRightId === 137)),
      inventoryManagement: Boolean(
        profile?.activityRights?.some((right) => right.ActivityRightId === INVENTORY_MANAGEMENT),
      ),
      inventoryManagementViewOnly: Boolean(
        profile?.activityRights?.some((right) => right.ActivityRightId === VIEW_INVENTORY_MANAGEMENT) &&
          !profile?.activityRights?.some((right) => right.ActivityRightId === INVENTORY_MANAGEMENT),
      ),
      isGAFundUser: Boolean(
        profile?.hqId === 6153830 && !(profile?.isHqUser && profile?.firms?.some((firm) => firm.firmId === '3182462')),
      ),
      autopay: Boolean(isAutopayEnabled?.enabled),
      reports: !shouldApplyApprovedAttorneyRestrictions,
      dashboard: !shouldApplyApprovedAttorneyRestrictions,
      ratesAndFees: !shouldApplyApprovedAttorneyRestrictions,
      underwriting: !shouldApplyApprovedAttorneyRestrictions,
      serviceOrders: !shouldApplyApprovedAttorneyRestrictions,
      agencyVerificationLetters: !shouldApplyApprovedAttorneyRestrictions,
      propertyReports: !shouldApplyApprovedAttorneyRestrictions,
      statements: !shouldApplyApprovedAttorneyRestrictions,
      serviceOrderInvoices: !shouldApplyApprovedAttorneyRestrictions,
      accounting: !shouldApplyApprovedAttorneyRestrictions,
      addressBook: !shouldApplyApprovedAttorneyRestrictions,
      autoReporting: !shouldApplyApprovedAttorneyRestrictions,
      eSignatures: shouldApplyApprovedAttorneyRestrictions
        ? false
        : Boolean(
            profile?.activityRights?.some((right: ActivityRight) => right.ActivityRightId === CAN_MANAGE_ESIGNATURES),
          ),
      applyApprovedAttorneyRestrictions: shouldApplyApprovedAttorneyRestrictions,
      invoiceDashboard: Boolean(
        profile?.activityRights?.some((right: ActivityRight) => right.ActivityRightId === INVOICE_DASHBOARD),
      ),
    };
  }, [
    profile,
    isApprovedAttorneyAccount,
    userFirm,
    isMarketPlaceEnabled,
    isAutopayEnabled,
    approvedAttorneyRestrictionsEnabled,
    shouldApplyApprovedAttorneyRestrictions,
  ]);

  return {
    hasPermission,
    profile,
    userFirm,
    prismUserProfile,
    fileData,
    setFileData,
    setRatesAndFeeDrawerClicked,
    isFormDirty,
    setOpenRatesAndFeeDrawer,
    openRatesAndFeeDrawer,
    setOpenSaveModal,
  };
};
